import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgSettingsProvider } from './contexts/OrgSettingsContext';
import { InstallPrompt, UpdateBanner } from './components/PWAPrompts';
import { Layout } from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { useIsMobile } from './hooks/useIsMobile';
import MobileInvoices from './pages/mobile/MobileInvoices';
import MobileAttendance from './pages/mobile/MobileAttendance';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import Timesheet from './pages/Timesheet';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import AuditLog from './pages/AuditLog';
import Masters from './pages/Masters';
import Reimbursements from './pages/Reimbursements';
import Tickets from './pages/Tickets';
import Leave from './pages/Leave';
import Attendance from './pages/Attendance';
import Invoices from './pages/Invoices';
import DeletedItems from './pages/DeletedItems';
import Leads from './pages/Leads';
import Tenders from './pages/Tenders';
import StaffManagement from './pages/StaffManagement';
import ClientProfile from './pages/ClientProfile';
import Chat from './pages/Chat';
import OrgSettings from './pages/OrgSettings';
import MyRules from './pages/MyRules';
import Payroll from './pages/Payroll';
import Payments from './pages/Payments';
import { SuperAuthProvider, useSuperAuth } from './contexts/SuperAuthContext';
import SuperLogin from './pages/super/SuperLogin';
import SuperConsole from './pages/super/SuperConsole';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  // Wrap the page in an error boundary so a single crashing page shows a
  // readable error (with the sidebar intact) instead of whiting-out the app.
  // Keyed on the path so navigating to another page clears a prior crash.
  return user
    ? <Layout><ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary></Layout>
    : <Navigate to="/login" replace />;
};

// `isAdmin` follows the current view, so someone in partner view who types one
// of these URLs lands back on the dashboard until they flip the switch.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isHR, loading } = useAuth();
  if (loading) return null;
  return (isAdmin || isHR) ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const PartnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isPartner, loading } = useAuth();
  if (loading) return null;
  return (isAdmin || isPartner) ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

/**
 * Invoicing has two entirely different screens rather than one responsive
 * one. Chosen here at the route rather than inside Invoices.tsx so that the
 * mobile screen can import the wizard and payment modals back out of it
 * without the two modules becoming circular.
 */
const InvoicesScreen: React.FC = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileInvoices /> : <Invoices />;
};

// Invoices: all authenticated staff (task managers and partners are filtered server-side)
const InvoiceRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// The platform console is a separate application that happens to share a
// bundle. It has its own session (SuperAuthContext), never renders the tenant
// Layout, and must not depend on a firm being signed in — an operator and a
// firm user can be logged in simultaneously in one browser.
const SuperArea: React.FC = () => {
  const { admin, loading } = useSuperAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="login" element={admin ? <Navigate to="/super" replace /> : <SuperLogin />} />
      <Route path="" element={admin ? <SuperConsole /> : <Navigate to="/super/login" replace />} />
      <Route path="*" element={<Navigate to="/super" replace />} />
    </Routes>
  );
};

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return null;

  // Field staff get their own tiny application: the punch screen and nothing
  // else. Written as a separate route table rather than as guards sprinkled
  // through the main one, because "he must not see the rest of the app" is
  // then a property of the router itself — there is no route here that could
  // be reached, so none can be forgotten. Any URL they land on, including one
  // typed by hand or restored by the browser, ends at the same screen.
  if (user?.isFieldStaff) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/attendance" replace />} />
        {/*
          Deliberately outside Layout. The shell carries the staff sidebar and
          the phone tab bar — Tasks, Timesheets, Invoices — none of which this
          worker may open, and on a phone it would additionally answer
          /attendance with "this is a desktop screen". The punch screen brings
          its own header and sign-out, so it needs no shell at all.
          This branch only runs with a signed-in user, so no auth guard is
          needed here: signing out drops back to the normal route table.
        */}
        <Route path="/attendance" element={<MobileAttendance />} />
        <Route path="*" element={<Navigate to="/attendance" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/tasks" element={<PrivateRoute><Tasks /></PrivateRoute>} />
      <Route path="/timesheets" element={<PrivateRoute><Timesheet /></PrivateRoute>} />
      <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
      <Route path="/clients/:id" element={<PrivateRoute><ClientProfile /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="/udin" element={<Navigate to="/tasks" replace />} />
      <Route path="/reimbursements" element={<PrivateRoute><Reimbursements /></PrivateRoute>} />
      <Route path="/tickets" element={<PrivateRoute><Tickets /></PrivateRoute>} />
      <Route path="/leaves" element={<PrivateRoute><Leave /></PrivateRoute>} />
      {/* Merged pages — old standalone routes redirect to merged parent */}
      <Route path="/announcements" element={<Navigate to="/staff-management" replace />} />
      <Route path="/leave-calendar" element={<Navigate to="/leaves" replace />} />
      <Route path="/holidays" element={<Navigate to="/attendance" replace />} />
      <Route path="/staff-documents" element={<Navigate to="/staff-management" replace />} />
      <Route path="/staff-kpis" element={<Navigate to="/staff-management" replace />} />
      <Route path="/wip-report" element={<Navigate to="/reports" replace />} />

      <Route path="/attendance" element={<PrivateRoute><Attendance /></PrivateRoute>} />
      <Route path="/invoices" element={<PrivateRoute><InvoiceRoute><InvoicesScreen /></InvoiceRoute></PrivateRoute>} />
      {/* Reconciliation moved to a sub-tab inside /invoices — old direct URL redirects there. */}
      <Route path="/reconciliation" element={<Navigate to="/invoices?tab=reconciliation" replace />} />
      <Route path="/leads" element={<PrivateRoute><PartnerRoute><Leads /></PartnerRoute></PrivateRoute>} />
      <Route path="/tenders" element={<PrivateRoute><PartnerRoute><Tenders /></PartnerRoute></PrivateRoute>} />
      <Route path="/staff-management" element={<PrivateRoute><AdminRoute><StaffManagement /></AdminRoute></PrivateRoute>} />
      <Route path="/payroll" element={<PrivateRoute><Payroll /></PrivateRoute>} />
      <Route path="/payments" element={<PrivateRoute><Payments /></PrivateRoute>} />
      <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
      <Route path="/rules" element={<PrivateRoute><MyRules /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/admin" element={<Navigate to="/staff-management" replace />} />
      <Route path="/audit" element={<PrivateRoute><AdminRoute><AuditLog /></AdminRoute></PrivateRoute>} />
      <Route path="/masters" element={<PrivateRoute><AdminRoute><Masters /></AdminRoute></PrivateRoute>} />
      <Route path="/org-settings" element={<PrivateRoute><AdminOnlyRoute><OrgSettings /></AdminOnlyRoute></PrivateRoute>} />
      <Route path="/deleted-items" element={<PrivateRoute><AdminOnlyRoute><DeletedItems /></AdminOnlyRoute></PrivateRoute>} />
      {/* Platform console — deliberately outside PrivateRoute: it uses its own
          operator session, not a firm login. */}
      <Route path="/super/*" element={<SuperAuthProvider><SuperArea /></SuperAuthProvider>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <OrgSettingsProvider>
        <AppRoutes />
        <InstallPrompt />
        <UpdateBanner />
      </OrgSettingsProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
